import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiGet } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import ODataTable from '../../components/reusable/ODataTable'
import OPagination from '../../components/reusable/OPagination'
import OSelect from '../../components/reusable/OSelect'
import { AiOutlineEye } from 'react-icons/ai'

const getCountryName = (code) => {
  if (!code) return code
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(
      String(code).toUpperCase()
    )
    return name || code
  } catch {
    return code
  }
}

const Applications = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1)
  const keyword = searchParams.get('keyword') || ''
  const status = searchParams.get('status') || ''
  const careerId = searchParams.get('careerId') || ''
  const nationality = searchParams.get('nationality') || ''
  const joinTime = searchParams.get('joinTime') || ''
  const employmentType = searchParams.get('employmentType') || ''
  const experienceLevel = searchParams.get('experienceLevel') || ''

  const [list, setList] = useState([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ page: 1, pageCount: 0, totalItems: 0, limit: 10 })
  const [sort, setSort] = useState({ sortKey: 'createdAt', sortType: 'desc' })
  const [keywordInput, setKeywordInput] = useState(keyword)
  const [nationalityOptions, setNationalityOptions] = useState([
    { value: '', key: t('All Nationalities') || 'All Nationalities' },
  ])
  const [careerOptions, setCareerOptions] = useState([
    { value: '', key: t('All Job Posts') || 'All Job Posts' },
  ])

  const updateParams = (patch, { resetPage = false } = {}) => {
    const next = new URLSearchParams(searchParams)
    Object.entries(patch).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) {
        next.delete(key)
      } else {
        next.set(key, String(value))
      }
    })
    if (resetPage) next.set('page', '1')
    setSearchParams(next)
  }

  const fetchCareerOptions = async (selectedCareerId = '') => {
    try {
      const params = new URLSearchParams()
      params.set('status', 'active')
      params.set('pageSize', '100')
      params.set('sortKey', 'title')
      params.set('sortType', 'asc')
      const res = await apiGet(`${apiPath.careers}?${params.toString()}`)
      const docs = res?.data?.results?.docs || []
      const options = [
        { value: '', key: t('All Job Posts') || 'All Job Posts' },
        ...docs.map((career) => ({
          value: career._id,
          key: career.title,
        })),
      ]

      if (
        selectedCareerId &&
        !options.some((opt) => String(opt.value) === String(selectedCareerId))
      ) {
        try {
          const detailRes = await apiGet(`${apiPath.careerById}/${selectedCareerId}`)
          const career = detailRes?.data?.results
          if (career?._id) {
            options.push({ value: career._id, key: career.title })
          }
        } catch {
          // Keep filter working even if selected career detail cannot be loaded
        }
      }

      setCareerOptions(options)
    } catch {
      setCareerOptions([{ value: '', key: t('All Job Posts') || 'All Job Posts' }])
    }
  }

  const fetchApplications = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      params.set('page', page)
      params.set('pageSize', pagination.limit)
      if (sort?.sortKey) params.set('sortKey', sort.sortKey)
      if (sort?.sortType) params.set('sortType', sort.sortType)
      if (keyword) params.set('keyword', keyword)
      if (status) params.set('status', status)
      if (careerId) params.set('careerId', careerId)
      if (nationality) params.set('nationality', nationality)
      if (joinTime) params.set('joinTime', joinTime)
      if (employmentType) params.set('employmentType', employmentType)
      if (experienceLevel) params.set('experienceLevel', experienceLevel)
      const res = await apiGet(`${apiPath.careerApplicationsList}?${params.toString()}`)
      const results = res?.data?.results || {}
      const items = results?.docs || []
      setList(items)
      setPagination({
        page: results.page || page,
        pageCount: results.totalPages || 0,
        totalItems: results.totalDocs || 0,
        limit: results.limit || pagination.limit
      })
      if (Array.isArray(results.nationalities)) {
        setNationalityOptions([
          { value: '', key: t('All Nationalities') || 'All Nationalities' },
          ...results.nationalities
            .map((item) => ({
              value: item,
              key: getCountryName(item),
            }))
            .sort((a, b) => a.key.localeCompare(b.key)),
        ])
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCareerOptions(careerId)
  }, [careerId])

  useEffect(() => {
    fetchApplications()
  }, [page, status, careerId, keyword, nationality, joinTime, employmentType, experienceLevel, sort])

  useEffect(() => {
    setKeywordInput(keyword)
  }, [keyword])

  useEffect(() => {
    const handler = setTimeout(() => {
      if (keywordInput !== keyword) {
        updateParams({ keyword: keywordInput }, { resetPage: true })
      }
    }, 300)
    return () => clearTimeout(handler)
  }, [keywordInput])

  const onPageChange = ({ selected }) => {
    updateParams({ page: selected + 1 })
  }

  const listQuery = searchParams.toString()
  const listPath = listQuery
    ? `/careers/applications?${listQuery}`
    : '/careers/applications'

  const columns = [
    { title: t('O_FULL_NAME'), key: 'fullName', sortable: true },
    { title: t('O_EMAIL_ADDRESS'), key: 'email', sortable: true },
    { title: t('PHONE'), key: 'phoneNumber', sortable: true },
    { title: t('CAREERS_CAREER'), key: 'careerTitle', sortable: true, render: (row) => row?.careerId?.title || row?.careerTitle || '-' },
    { title: t('O_STATUS'), key: 'status', sortable: true, align: 'center' },
    { title: t('CAREERS_APPLIED_AT'), key: 'createdAt', sortable: true, render: (row) => new Date(row.createdAt).toLocaleString() },
    { title: t('O_ACTION'), key: 'actions', align: 'center', render: (row) => (
      <div className='flex gap-2 justify-center items-center'>
        <button
          className='hover:text-blue-700 w-[18px] h-[18px] flex items-center justify-center'
          title={t('O_VIEW')}
          onClick={() =>
            navigate(`/careers/applications/view/${row._id}`, {
              state: { returnTo: listPath },
            })
          }
        >
          <AiOutlineEye size={18} />
        </button>
      </div>
    )}
  ]

  const statusOptions = [
    { value: '', key: t('All Statuses') || 'All Statuses' },
    { value: 'pending', key: 'pending' },
    { value: 'reviewing', key: 'reviewing' },
    { value: 'shortlisted', key: 'shortlisted' },
    { value: 'interviewed', key: 'interviewed' },
    { value: 'accepted', key: 'accepted' },
    { value: 'rejected', key: 'rejected' }
  ]

  const joinTimeOptions = [
    { value: '', key: t('All Join Times') || 'All Join Times' },
    { value: 'immediate', key: t('Immediate') || 'Immediate' },
    { value: '1month', key: t('One Month') || 'One Month' },
    { value: '3months', key: t('Three Months') || 'Three Months' },
  ]

  const employmentTypeOptions = [
    { value: '', key: t('All Employment Types') || 'All Employment Types' },
    { value: 'full-time', key: t('full-time') || 'Full-time' },
    { value: 'part-time', key: t('part-time') || 'Part-time' },
    { value: 'contract', key: t('contract') || 'Contract' },
    { value: 'internship', key: t('internship') || 'Internship' },
    { value: 'remote', key: t('remote') || 'Remote' },
  ]

  const experienceLevelOptions = [
    { value: '', key: t('All Experience Levels') || 'All Experience Levels' },
    { value: 'entry', key: t('entry') || 'Entry' },
    { value: 'mid', key: t('mid') || 'Mid' },
    { value: 'senior', key: t('senior') || 'Senior' },
    { value: 'executive', key: t('executive') || 'Executive' },
  ]

  return (
    <div>
      <div className='bg-white'>
        <div className='px-3 py-4'>
          <div className='bg-white border border-[#E9EDF9]'>
            <div className='border-b border-b-[#E3E3E3] flex flex-wrap items-end gap-2 px-4 py-3'>
              <div className='w-full md:w-60'>
                <label className='block mb-2 text-sm font-medium text-gray-900'>{t('O_SEARCH')}</label>
                <input
                  className='bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5'
                  placeholder={t('SEARCH_BY_KEYWORD')}
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                />
              </div>
              <div className='w-full md:w-52'>
                <OSelect
                  inputLabel={t('O_STATUS')}
                  name='status'
                  register={{
                    name: 'status',
                    onChange: (e) => updateParams({ status: e.target.value }, { resetPage: true }),
                  }}
                  errors={{}}
                  options={statusOptions}
                  value={status}
                  wrapperClassName='w-full'
                />
              </div>
              <div className='w-full md:w-52'>
                <OSelect
                  inputLabel={t('CAREERS_CAREER')}
                  name='careerId'
                  register={{
                    name: 'careerId',
                    onChange: (e) =>
                      updateParams({ careerId: e.target.value }, { resetPage: true }),
                  }}
                  errors={{}}
                  options={careerOptions}
                  value={careerId}
                  wrapperClassName='w-full'
                />
              </div>
              <div className='w-full md:w-52'>
                <OSelect
                  inputLabel={t('Nationality') || 'Nationality'}
                  name='nationality'
                  register={{
                    name: 'nationality',
                    onChange: (e) =>
                      updateParams({ nationality: e.target.value }, { resetPage: true }),
                  }}
                  errors={{}}
                  options={nationalityOptions}
                  value={nationality}
                  wrapperClassName='w-full'
                />
              </div>
              <div className='w-full md:w-52'>
                <OSelect
                  inputLabel={t('Ready to join in') || 'Ready to join in'}
                  name='joinTime'
                  register={{
                    name: 'joinTime',
                    onChange: (e) =>
                      updateParams({ joinTime: e.target.value }, { resetPage: true }),
                  }}
                  errors={{}}
                  options={joinTimeOptions}
                  value={joinTime}
                  wrapperClassName='w-full'
                />
              </div>
              <div className='w-full md:w-52'>
                <OSelect
                  inputLabel={t('CAREERS_EMPLOYMENT_TYPE') || 'Employment Type'}
                  name='employmentType'
                  register={{
                    name: 'employmentType',
                    onChange: (e) =>
                      updateParams({ employmentType: e.target.value }, { resetPage: true }),
                  }}
                  errors={{}}
                  options={employmentTypeOptions}
                  value={employmentType}
                  wrapperClassName='w-full'
                />
              </div>
              <div className='w-full md:w-52'>
                <OSelect
                  inputLabel={t('Experience') || 'Experience Level'}
                  name='experienceLevel'
                  register={{
                    name: 'experienceLevel',
                    onChange: (e) =>
                      updateParams({ experienceLevel: e.target.value }, { resetPage: true }),
                  }}
                  errors={{}}
                  options={experienceLevelOptions}
                  value={experienceLevel}
                  wrapperClassName='w-full'
                />
              </div>
            </div>

            <ODataTable
              data={list}
              columns={columns}
              loading={loading}
              pagination={pagination}
              onPageChange={onPageChange}
              sort={sort}
              onSortChange={(nextSort) => {
                setSort(nextSort)
                updateParams({ page: 1 })
              }}
            />

            <OPagination pagination={pagination} onPageChange={onPageChange} page={pagination.page} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Applications
